/**
 * lottie-init.js — Self-hosted Lottie hydration controller
 * ─────────────────────────────────────────────────────────
 * • Finds all [data-lottie-asset] containers rendered by snippets/lottie.liquid
 * • Lazy-loads via IntersectionObserver (only inits when visible)
 * • Uses lottie-web (lottie.min.js) loaded in theme.liquid
 * • Handles Shopify section lifecycle (load / unload / reorder)
 * • Dedupes instances — no double renders
 * • On fetch/parse error → renders fallback image or inline SVG placeholder
 * • Respects prefers-reduced-motion
 */
(function () {
	"use strict";

	var SELECTOR = "[data-lottie-asset]";
	var INIT_ATTR = "data-lottie-ready";
	var instances = {}; // id → lottie animation instance

	/* ── Reduced motion check ── */
	var prefersReduced =
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	/* ── Wait for lottie-web to be available ── */
	function getLottie() {
		return window.lottie || window.bodymovin || null;
	}

	/* ── Render a single container ── */
	function hydrate(el) {
		// Skip if already initialised or no asset URL
		if (el.hasAttribute(INIT_ATTR)) return;
		el.setAttribute(INIT_ATTR, "true");

		var assetUrl = el.getAttribute("data-lottie-asset");
		var loop = el.getAttribute("data-lottie-loop") !== "false";
		var autoplay = el.getAttribute("data-lottie-autoplay") !== "false";
		var speed = parseFloat(el.getAttribute("data-lottie-speed")) || 1;
		var fallbackUrl = el.getAttribute("data-lottie-fallback") || "";
		var id = el.id || "lottie-" + Math.random().toString(36).slice(2, 8);

		if (!assetUrl) {
			showFallback(el, fallbackUrl);
			return;
		}

		// Reduced motion → show first frame only (no animation)
		if (prefersReduced) {
			autoplay = false;
			loop = false;
		}

		var lib = getLottie();
		if (!lib) {
			// lottie-web not loaded yet — retry with backoff (up to ~8s total)
			var retryCount = parseInt(
				el.getAttribute("data-lottie-retries") || "0",
				10,
			);
			if (retryCount < 5) {
				el.setAttribute("data-lottie-retries", String(retryCount + 1));
				setTimeout(
					function () {
						el.removeAttribute(INIT_ATTR);
						hydrate(el);
					},
					500 * Math.pow(2, retryCount),
				);
			} else {
				console.warn(
					"[lottie-init] lottie-web not available after 5 retries — showing fallback",
				);
				showFallback(el, fallbackUrl);
			}
			return;
		}

		// Destroy previous instance if exists (re-init safe)
		destroyInstance(id);

		// Fetch the JSON, then init
		fetch(assetUrl)
			.then(function (res) {
				if (!res.ok) throw new Error("HTTP " + res.status);
				return res.json();
			})
			.then(function (animationData) {
				// Clear any existing content
				el.innerHTML = "";

				var anim = lib.loadAnimation({
					container: el,
					renderer: "svg",
					loop: loop,
					autoplay: autoplay,
					animationData: animationData,
					rendererSettings: {
						preserveAspectRatio: "xMidYMid meet",
						progressiveLoad: true,
					},
				});
				anim.setSpeed(speed);
				instances[id] = anim;
			})
			.catch(function () {
				showFallback(el, fallbackUrl);
			});
	}

	/* ── Fallback: image or inline SVG placeholder ── */
	function showFallback(el, fallbackUrl) {
		if (fallbackUrl) {
			el.innerHTML =
				'<img src="' +
				fallbackUrl +
				'" alt="Animation" loading="lazy" style="width:100%;height:100%;object-fit:contain;">';
		} else {
			// Minimal inline car SVG placeholder
			el.innerHTML =
				'<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;opacity:.35;">' +
				'<rect x="10" y="18" width="80" height="26" rx="6" fill="#98a2b3"/>' +
				'<rect x="30" y="6" width="40" height="20" rx="4" fill="#98a2b3"/>' +
				'<circle cx="30" cy="48" r="8" fill="#667085"/>' +
				'<circle cx="72" cy="48" r="8" fill="#667085"/>' +
				'<rect x="5" y="54" width="110" height="2" rx="1" fill="#d0d5dd"/>' +
				"</svg>";
		}
	}

	/* ── Destroy a specific instance ── */
	function destroyInstance(id) {
		if (instances[id]) {
			try {
				instances[id].destroy();
			} catch (_) {}
			delete instances[id];
		}
	}

	/* ── Destroy all (for section unload) ── */
	function destroyAll() {
		Object.keys(instances).forEach(destroyInstance);
	}

	/* ── Init all visible containers ── */
	function initAll() {
		var els = document.querySelectorAll(SELECTOR);
		if (!els.length) return;

		if ("IntersectionObserver" in window) {
			var observer = new IntersectionObserver(
				function (entries) {
					entries.forEach(function (entry) {
						if (entry.isIntersecting) {
							hydrate(entry.target);
							observer.unobserve(entry.target);
						}
					});
				},
				{ rootMargin: "200px" },
			);
			els.forEach(function (el) {
				if (!el.hasAttribute(INIT_ATTR)) {
					observer.observe(el);
				}
			});
		} else {
			// Fallback: hydrate everything immediately
			els.forEach(hydrate);
		}
	}

	/* ── Re-init (for new section content) ── */
	function reinit() {
		// Find any un-initialised containers and hydrate them
		var els = document.querySelectorAll(SELECTOR + ":not([" + INIT_ATTR + "])");
		els.forEach(hydrate);
	}

	/* ── Shopify Section lifecycle ── */
	document.addEventListener("shopify:section:load", reinit);
	document.addEventListener("shopify:section:reorder", reinit);
	document.addEventListener("shopify:section:unload", function (e) {
		// Destroy instances inside the unloaded section
		if (e.target) {
			var els = e.target.querySelectorAll(SELECTOR);
			els.forEach(function (el) {
				var id = el.id || "";
				if (id && instances[id]) destroyInstance(id);
				el.removeAttribute(INIT_ATTR);
			});
		}
	});

	/* ── Expose for programmatic use ── */
	window.__lottieInit = {
		hydrate: hydrate,
		reinit: reinit,
		destroyAll: destroyAll,
		instances: instances,
	};

	/* ── Boot ── */
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initAll);
	} else {
		initAll();
	}
})();
